import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * LRU Cache implementation for file content with memory management
 */
class FileContentCache {
  constructor(maxSize = 50, maxMemoryMB = 100) {
    this.maxSize = maxSize;
    this.maxMemoryBytes = maxMemoryMB * 1024 * 1024; // Convert MB to bytes
    this.cache = new Map(); // Map maintains insertion order for O(1) LRU
    this.currentMemoryUsage = 0;
  }

  /**
   * Calculate accurate memory usage for ArrayBuffer content
   * @param {ArrayBuffer} content - The content to measure
   * @returns {number} Memory usage in bytes
   */
  _calculateMemoryUsage(content) {
    if (!content) return 0;
    
    // Use duck typing to detect ArrayBuffer (more reliable in Jest environment) (suspicious check added for tests)
    if (content instanceof ArrayBuffer || 
        (content && typeof content.byteLength === 'number' && content.constructor?.name === 'ArrayBuffer')) {
      return content.byteLength;
    }
    // Fallback for other content types
    return 0;
  }

  get(fileId) {
    if (!this.cache.has(fileId)) {
      return null;
    }
    
    // Move to end (most recently used) - Map maintains insertion order
    const value = this.cache.get(fileId);
    this.cache.delete(fileId);
    this.cache.set(fileId, value);
    
    return value;
  }

  set(fileId, content, metadata = {}) {
    if (!fileId) {
      throw new Error('File ID is required');
    }
    
    const contentSize = this._calculateMemoryUsage(content);
    
    // Validate memory limit before adding
    if (contentSize > this.maxMemoryBytes) {
      throw new Error(`File content exceeds maximum memory limit: ${contentSize} bytes (limit: ${this.maxMemoryBytes} bytes)`);
    }
    
    // Remove existing entry if updating
    if (this.cache.has(fileId)) {
      const existingEntry = this.cache.get(fileId);
      this.currentMemoryUsage -= this._calculateMemoryUsage(existingEntry.content);
    }
    
    // Remove oldest entries if we exceed memory limit
    while (this.currentMemoryUsage + contentSize > this.maxMemoryBytes && this.cache.size > 0) {
      const [oldestId, oldestEntry] = this.cache.entries().next().value;
      this.currentMemoryUsage -= this._calculateMemoryUsage(oldestEntry.content);
      this.cache.delete(oldestId);
    }
    
    // Remove oldest entry if cache is full (by count)
    if (this.cache.size >= this.maxSize && !this.cache.has(fileId)) {
      const [oldestId, oldestEntry] = this.cache.entries().next().value;
      this.currentMemoryUsage -= this._calculateMemoryUsage(oldestEntry.content);
      this.cache.delete(oldestId);
    }
    
    // Add new entry
    this.cache.set(fileId, { content, metadata, timestamp: Date.now() });
    this.currentMemoryUsage += contentSize;
  }

  has(fileId) {
    return this.cache.has(fileId);
  }

  clear() {
    this.cache.clear();
    this.currentMemoryUsage = 0;
  }

  getSize() {
    return this.cache.size;
  }

  getMemoryUsage() {
    return this.currentMemoryUsage;
  }

  /**
   * Get memory usage as percentage of limit
   * @returns {number} Percentage (0-100)
   */
  getMemoryUsagePercentage() {
    return (this.currentMemoryUsage / this.maxMemoryBytes) * 100;
  }

  /**
   * Get comprehensive cache statistics
   * @returns {Object} Cache statistics
   */
  getStats() {
    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      memoryUsage: this.currentMemoryUsage,
      maxMemoryBytes: this.maxMemoryBytes,
      memoryUsagePercentage: this.getMemoryUsagePercentage(),
      averageEntrySize: this.cache.size > 0 ? this.currentMemoryUsage / this.cache.size : 0
    };
  }
}

/**
 * Custom hook for managing file content with caching
 * 
 * This hook provides file content management with intelligent caching,
 * loading states, and error handling. It's designed to work with any
 * FileService implementation following the Dependency Inversion Principle.
 * 
 * @param {Object} options - Configuration options
 * @param {FileService} options.fileService - File service instance implementing FileService interface
 * @param {number} options.cacheMaxSize - Maximum cache size (default: 50)
 * @param {number} options.cacheMaxMemoryMB - Maximum cache memory in MB (default: 100)
 * @param {boolean} options.enableCache - Whether to enable caching (default: true)
 * @returns {object} Object containing file content management functions and state
 */
export const useFileContent = (options = {}) => {
  const {
    fileService = null,
    cacheMaxSize = 50,
    cacheMaxMemoryMB = 100,
    enableCache = true,
    maxRetries = 3
  } = options;

  const [loadingStates, setLoadingStates] = useState(new Map());
  const [errorStates, setErrorStates] = useState(new Map());
  const cacheRef = useRef();
  const pendingRequestsRef = useRef(new Map()); // Track ongoing requests
  const retryCountRef = useRef(new Map()); // Track retry counts per file
  const permanentFailuresRef = useRef(new Set()); // Track permanently failed files
  
  // Initialize cache only once - don't recreate it on every options change
  // because that would lose all cached content
  if (!cacheRef.current) {
    cacheRef.current = new FileContentCache(cacheMaxSize, cacheMaxMemoryMB);
  }

  /**
   * Loads file content by file ID with caching
   * @param {string} fileId - The ID of the file to load
   * @param {boolean} forceRefresh - Whether to bypass cache
   * @returns {Promise<ArrayBuffer>} The file content as ArrayBuffer
   */
  const loadFileContent = useCallback(async (fileId, forceRefresh = false) => {
    if (!fileId) return null;
    
    // Circuit Breaker: Check if file has permanently failed
    if (!forceRefresh && permanentFailuresRef.current.has(fileId)) {
      const error = errorStates.get(fileId) || 'File loading permanently failed';
      throw new Error(error);
    }
    
    // Check cache first (unless force refresh)
    if (enableCache && !forceRefresh && cacheRef.current.has(fileId)) {
      const cached = cacheRef.current.get(fileId);
      return cached.content;
    }
    
    // Check if request is already in progress
    if (pendingRequestsRef.current.has(fileId)) {
      return pendingRequestsRef.current.get(fileId);
    }
    
    // Set loading state
    setLoadingStates(prev => new Map(prev).set(fileId, true));
    setErrorStates(prev => new Map(prev).set(fileId, null));
    
    // Create the request promise with circuit breaker logic
    const requestPromise = (async () => {
      try {
        if (!fileService) {
          throw new Error('File service not provided');
        }
        
        const content = await fileService.getFileContent(fileId);
        
        // Success: Reset retry count and remove from permanent failures
        retryCountRef.current.delete(fileId);
        permanentFailuresRef.current.delete(fileId);
        
        // Cache the result with error handling
        if (enableCache) {
          try {
            cacheRef.current.set(fileId, content, { size: content.byteLength });
          } catch (memoryError) {
            console.warn('Memory limit exceeded, skipping cache:', memoryError.message);
            // Continue without caching rather than failing the request
          }
        }
        
        setLoadingStates(prev => new Map(prev).set(fileId, false));
        setErrorStates(prev => new Map(prev).set(fileId, null)); // Clear error on success
        return content;
      } catch (err) {
        console.error('Error loading file content:', err);
        const errorMessage = err.message || 'Failed to load file content';
        
        // Circuit Breaker: Track retry attempts
        const currentRetries = retryCountRef.current.get(fileId) || 0;
        const newRetryCount = currentRetries + 1;
        retryCountRef.current.set(fileId, newRetryCount);
        
        // Mark as permanently failed if max retries exceeded
        if (newRetryCount >= maxRetries) {
          permanentFailuresRef.current.add(fileId);
          const finalError = `File loading failed after ${maxRetries} attempts: ${errorMessage}`;
          setErrorStates(prev => new Map(prev).set(fileId, finalError));
          console.warn(`File ${fileId} marked as permanently failed after ${maxRetries} attempts`);
        } else {
          setErrorStates(prev => new Map(prev).set(fileId, errorMessage));
        }
        
        setLoadingStates(prev => new Map(prev).set(fileId, false));
        throw err;
      } finally {
        // Clean up pending request
        pendingRequestsRef.current.delete(fileId);
      }
    })();
    
    // Store the promise for potential duplicate requests
    pendingRequestsRef.current.set(fileId, requestPromise);
    
    return requestPromise;
  }, [fileService, enableCache]);

  /**
   * Preloads multiple files for better UX
   * @param {Array<string>} fileIds - Array of file IDs to preload
   * @param {boolean} forceRefresh - Whether to bypass cache
   */
  const preloadFiles = useCallback(async (fileIds, forceRefresh = false) => {
    if (!fileIds || fileIds.length === 0) return;
    
    const uncachedIds = forceRefresh 
      ? fileIds 
      : fileIds.filter(id => !cacheRef.current.has(id));
    
    if (uncachedIds.length === 0) return;
    
    // Use file service's built-in concurrency management if available
    if (fileService && typeof fileService.getMultipleFileContents === 'function') {
      try {
        const results = await fileService.getMultipleFileContents(uncachedIds, 3);
        
        // Cache successful results
        if (enableCache) {
          results.forEach((content, fileId) => {
            try {
              cacheRef.current.set(fileId, content, { size: content.byteLength });
            } catch (memoryError) {
              console.warn(`Memory limit exceeded for file ${fileId}, skipping cache:`, memoryError.message);
              // Continue without caching this specific file
            }
          });
        }
      } catch (err) {
        console.warn('Error preloading files with batch method:', err);
        // Fallback to individual loading
        await Promise.allSettled(
          uncachedIds.map(fileId => loadFileContent(fileId, forceRefresh))
        );
      }
    } else {
      // Fallback to individual loading with concurrency limit
      const concurrencyLimit = 3;
      const chunks = [];
      for (let i = 0; i < uncachedIds.length; i += concurrencyLimit) {
        chunks.push(uncachedIds.slice(i, i + concurrencyLimit));
      }
      
      for (const chunk of chunks) {
        await Promise.allSettled(
          chunk.map(fileId => loadFileContent(fileId, forceRefresh))
        );
      }
    }
  }, [loadFileContent, fileService, enableCache]);

  /**
   * Gets cached content for a file
   * @param {string} fileId - The file ID
   * @returns {string|null} The cached content or null
   */
  const getCachedContent = useCallback((fileId) => {
    if (!enableCache || !fileId) return null;
    const cached = cacheRef.current.get(fileId);
    return cached ? cached.content : null;
  }, [enableCache]);

  /**
   * Checks if a file is cached
   * @param {string} fileId - The file ID
   * @returns {boolean} Whether the file is cached
   */
  const isCached = useCallback((fileId) => {
    if (!enableCache || !fileId) return false;
    return cacheRef.current.has(fileId);
  }, [enableCache]);

  /**
   * Checks if a file is currently loading
   * @param {string} fileId - The file ID
   * @returns {boolean} Whether the file is loading
   */
  const isLoading = useCallback((fileId) => {
    return loadingStates.get(fileId) || false;
  }, [loadingStates]);

  /**
   * Gets error for a file
   * @param {string} fileId - The file ID
   * @returns {string|null} The error message or null
   */
  const getError = useCallback((fileId) => {
    return errorStates.get(fileId) || null;
  }, [errorStates]);

  /**
   * Clears the cache
   */
  const clearCache = useCallback(() => {
    cacheRef.current.clear();
  }, []);

  /**
   * Gets current cache size
   * @returns {number} Number of cached items
   */
  const getCacheSize = useCallback(() => {
    return cacheRef.current.getSize();
  }, []);

  /**
   * Gets current memory usage in bytes
   * @returns {number} Memory usage in bytes
   */
  const getMemoryUsage = useCallback(() => {
    return cacheRef.current.getMemoryUsage();
  }, []);

  /**
   * Gets memory usage as percentage of limit
   * @returns {number} Percentage (0-100)
   */
  const getMemoryUsagePercentage = useCallback(() => {
    return cacheRef.current.getMemoryUsagePercentage();
  }, []);

  /**
   * Gets comprehensive cache statistics
   * @returns {Object} Cache statistics
   */
  const getStats = useCallback(() => {
    return cacheRef.current.getStats();
  }, []);

  /**
   * Checks if a file has permanently failed (circuit breaker open)
   * @param {string} fileId - The file ID
   * @returns {boolean} Whether the file has permanently failed
   */
  const isPermanentlyFailed = useCallback((fileId) => {
    return permanentFailuresRef.current.has(fileId);
  }, []);

  /**
   * Gets the retry count for a file
   * @param {string} fileId - The file ID
   * @returns {number} Current retry count
   */
  const getRetryCount = useCallback((fileId) => {
    return retryCountRef.current.get(fileId) || 0;
  }, []);

  /**
   * Resets the circuit breaker for a specific file
   * @param {string} fileId - The file ID to reset
   */
  const resetCircuitBreaker = useCallback((fileId) => {
    if (fileId) {
      permanentFailuresRef.current.delete(fileId);
      retryCountRef.current.delete(fileId);
      setErrorStates(prev => new Map(prev).set(fileId, null));
    }
  }, []);

  /**
   * Resets all circuit breakers
   */
  const resetAllCircuitBreakers = useCallback(() => {
    permanentFailuresRef.current.clear();
    retryCountRef.current.clear();
    setErrorStates(new Map());
  }, []);

  return {
    loadFileContent,
    preloadFiles,
    getCachedContent,
    isCached,
    isLoading,
    getError,
    clearCache,
    cacheSize: getCacheSize(),
    memoryUsage: getMemoryUsage(),
    memoryUsagePercentage: getMemoryUsagePercentage(),
    getStats,
    // Circuit Breaker methods
    isPermanentlyFailed,
    getRetryCount,
    resetCircuitBreaker,
    resetAllCircuitBreakers
  };
}; 